/**
 * Special Card Component
 * A special is shown as its own extra card beside the hole cards — that
 * card's finished art (no rank, suit or jewel), with its name above it, so
 * it's easy to recognise. The hole card it belongs to gently pulses
 * (PlayingCard). Long-press (or Enter/Space) shows the card full size; a tap
 * on it turns it over to its effect (SpecialCardViewer).
 *
 * Until the 4 corners are revealed the holder only knows the card is special:
 * it shows as the gold "Special ?" card, and turns over to the real one after.
 */

import React, { useEffect, useRef, useState } from 'react';
import { SPECIAL_CARD_INFO, SpecialCardSign, type SpecialCardType } from '@pokercubed/shared';
import { SPECIAL_BACK_ART, SPECIAL_BLANK_ART, SPECIAL_CARD_ART } from '../../assets/cards';
import SpecialCardViewer from './SpecialCardViewer';

/** How long a press has to be held before the effect opens. */
const LONG_PRESS_MS = 450;

/** Shown for a special not turned over: your own until the 4 corners, an opponent's until a showdown. */
const UNREVEALED = {
  name: 'Special',
  description:
    'A special card — it could Help or Hinder. Its holder sees which once the 4 corners are revealed; everyone else only at a showdown (or with a Spy).',
};

/** The labels players see for the two kinds of special. */
export const SIGN_LABEL: Record<SpecialCardSign, string> = {
  [SpecialCardSign.POSITIVE]: 'Help',
  [SpecialCardSign.NEGATIVE]: 'Hinder',
};

interface SpecialCardProps {
  special: SpecialCardType | 'unrevealed';
  /** Sizes the card itself (e.g. a width class). */
  className?: string;
  /** Show the card's name above it — off where there's no room (opponent rows). */
  showName?: boolean;
  /**
   * Insight cards, once active: a tap (not a long-press) calls this — it
   * opens the opponents' hands full size — and an arrow on the card says so.
   */
  onView?: () => void;
  /** A tap opens it full size too, not just a long-press (the Special Cards page). */
  tapToOpen?: boolean;
}

const SpecialCard: React.FC<SpecialCardProps> = ({ special, className = '', showName = true, onView, tapToOpen = false }) => {
  const unrevealed = special === 'unrevealed';
  const info = unrevealed ? null : SPECIAL_CARD_INFO[special];
  const name = info?.name ?? UNREVEALED.name;
  const description = info?.description ?? UNREVEALED.description;
  const art = unrevealed ? SPECIAL_BACK_ART : SPECIAL_CARD_ART[special];
  const [open, setOpen] = useState(false);
  // Turn over (a flip) when a "?" becomes the real card.
  const [turning, setTurning] = useState(false);
  const wasUnrevealed = useRef(unrevealed);
  useEffect(() => {
    if (wasUnrevealed.current && !unrevealed) setTurning(true);
    wasUnrevealed.current = unrevealed;
  }, [unrevealed]);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Set when a press became a long-press, so the click that may follow it isn't taken as a tap.
  const longPressed = useRef(false);

  const cancelPress = () => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
  };

  useEffect(() => cancelPress, []);

  return (
    <>
      <div
        role="button"
        tabIndex={0}
        aria-label={`${name} special card — ${onView ? 'tap to see opponents’ hands, ' : ''}long-press to see it full size`}
        data-testid={`special-card-${special}`}
        title={`${name}: ${description}`}
        className={`relative aspect-[5/7] select-none touch-manipulation cursor-pointer ${
          turning ? 'special-turn-over' : ''
        } ${className}`}
        onAnimationEnd={() => setTurning(false)}
        style={{ WebkitTouchCallout: 'none' }}
        onPointerDown={() => {
          cancelPress();
          longPressed.current = false;
          timer.current = setTimeout(() => {
            longPressed.current = true;
            setOpen(true);
          }, LONG_PRESS_MS);
        }}
        onPointerUp={cancelPress}
        onPointerLeave={cancelPress}
        onPointerCancel={cancelPress}
        // A tap (a click that wasn't a long-press): insight cards open the opponents' hands.
        onClick={() => {
          if (!longPressed.current && onView) onView();
          else if (!longPressed.current && tapToOpen) setOpen(true);
          longPressed.current = false;
        }}
        onContextMenu={(e) => e.preventDefault()}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setOpen(true);
          }
        }}
      >
        {/* Name just above the card, so the card itself lines up with the hole cards. */}
        {/* The face-down card's art already says "Special" — no label over it. */}
        {showName && !unrevealed && (
          <div className="absolute bottom-full mb-0.5 left-1/2 -translate-x-1/2 text-[8px] leading-none font-bold tracking-tight text-amber-200 whitespace-nowrap pointer-events-none">
            {name}
          </div>
        )}
        <img
          src={art ?? SPECIAL_BLANK_ART}
          alt={name}
          // Sized so the white frame lands exactly on a hole card's outline
          // (PlayingCard's frame art fills its box to ~2% in; a special's
          // frame sits 4.7% / 3.3% in, behind a rim that's nearly invisible
          // on the table) — that faint rim spills just outside the box.
          className="absolute max-w-none object-fill pointer-events-none"
          style={{ left: '-3.1%', top: '-2.0%', width: '106.2%', height: '104.1%' }}
          draggable={false}
        />
        {/* Insight card, active: tap to see the opponents' hands full size. */}
        {onView && (
          <span
            data-testid="view-hands-arrow"
            aria-hidden
            className="special-pulse absolute -bottom-1.5 -right-1.5 z-10 flex h-5 w-5 items-center justify-center rounded-full bg-amber-300 text-[11px] font-black leading-none text-gray-900 shadow-lg pointer-events-none"
          >
            ➜
          </span>
        )}
      </div>

      {open && <SpecialCardViewer special={special} onClose={() => setOpen(false)} />}
    </>
  );
};

export default SpecialCard;
